import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, ListOrdered } from "lucide-react";
import { DeductBalance } from "@/components/DeductBalance";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PriceBox = ({ m, reload }) => {
  const [v, setV] = useState(m.set_price);
  const save = async () => {
    try { await api.put(`/admin/live-transfers/mlo/${m.id}`, { price: Number(v) }); toast.success(`${m.name}'s price saved`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-slate-500">$</span>
      <Input type="number" min="1" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => Number(v) !== m.set_price && save()} className="h-8 w-24" data-testid={`queue-price-${m.id}`} />
    </div>
  );
};

const QueueRow = ({ m, i, last, bidding, move, reload }) => {
  const toggleFree = async (free) => {
    try { await api.put(`/admin/live-transfers/mlo/${m.id}`, { free }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 py-3 md:flex-row md:items-center" data-testid={`queue-row-${m.id}`}>
      <div className="flex items-center gap-3 md:w-1/3">
        {!bidding && <div className="flex flex-col">
          <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`queue-up-${m.id}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" disabled={last} onClick={() => move(i, 1)} data-testid={`queue-down-${m.id}`}><ArrowDown className="h-4 w-4" /></Button>
        </div>}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white" data-testid={`queue-rank-${m.id}`}>{m.rank || "–"}</div>
        <div><div className="font-semibold text-slate-900">{m.name}</div><div className="text-xs text-slate-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div></div>
      </div>
      <div className="text-sm md:w-1/6"><div className="text-xs text-slate-500">Balance</div><div className="font-semibold" data-testid={`queue-balance-${m.id}`}>{money(m.balance)}</div><DeductBalance m={m} reload={reload} /></div>
      <div className="md:w-1/6"><div className="text-xs text-slate-500">{bidding ? "Their bid" : "Price per transfer"}</div>
        {m.free ? <div className="text-sm font-semibold">Free</div> : bidding ? <div className="text-sm font-semibold">{money(m.bid)}</div> : <PriceBox m={m} reload={reload} />}</div>
      <label className="flex items-center gap-2 text-xs text-slate-600 md:w-1/6"><Switch checked={m.free} onCheckedChange={toggleFree} data-testid={`queue-free-${m.id}`} />Free transfers</label>
      <div className="md:flex-1">{m.rank ? <Badge className="border-0 bg-emerald-100 text-emerald-800" data-testid={`queue-status-${m.id}`}>{m.rank === 1 ? "Next up" : `#${m.rank} in line`}</Badge>
        : <Badge className="border-0 bg-slate-100 text-slate-700" data-testid={`queue-status-${m.id}`}>Skipped: {m.skip_reason}</Badge>}</div>
    </div>
  );
};

export const LiveTransferQueue = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/live-transfers/queue").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const move = async (i, dir) => {
    const ids = d.mlos.map((m) => m.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    try { await api.put("/admin/live-transfers/queue", { ids }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="mb-5 p-6" data-testid="live-transfer-queue">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><ListOrdered className="h-5 w-5" />Live transfer order</h3>
      </div>
      <p className="mb-3 text-sm text-slate-600" data-testid="queue-mode-note">Calls always go down this list in your order, and only to MLOs with live transfers on, a phone number and at least $100 in their balance. After an MLO gets a transfer they move to the back. MLOs who aren't eligible are skipped but keep their spot. If someone doesn't answer, the next MLO in line is rung (up to 2 in total), then Ava books an appointment. MLOs are only charged for transfers they answer.</p>
      {!d.mlos.length && <p className="text-sm text-slate-500">No active MLO accounts yet.</p>}
      {d.mlos.map((m, i) => <QueueRow key={m.id} m={m} i={i} last={i === d.mlos.length - 1} bidding={false} move={move} reload={load} />)}
    </Card>
  );
};
